import { Button, Col, Form, Input, Radio, Row, Select, Upload } from 'antd';
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import ReactMapGL from 'react-map-gl';
import { InboxOutlined } from '@ant-design/icons';
import * as toGeoJSON from '@mapbox/togeojson';

import './DeliveryAreaForm.scss';
import DrawControl from './DrawPolygonControl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import PostalCodeInput from '../../Elements/PostalCodesInput';

function polygonsToGeoJSON(area) {
  const features = area?.area?.coordinates.map((coordinates, index) => {
    return {
      type: 'Feature',
      properties: {
        id: index, // Assign a unique ID or pre-loaded areas
      },
      geometry: {
        type: 'Polygon',
        coordinates,
      },
    };
  });

  return {
    type: 'FeatureCollection',
    features: features || [],
  };
}

const kmlPolygonsToFeatureCollection = (polygon) => {
  const features = Object.entries(polygon).map(([id, coordinates]) => ({
    type: 'Feature',
    properties: { id: parseInt(id) },
    geometry: {
      type: 'Polygon',
      coordinates,
    },
  }));

  return {
    type: 'FeatureCollection',
    features,
  };
};

const DeliveryAreaForm = ({ isModalOpen, handleCancel, handleOk, area, isNewArea, carriers }) => {
  const mapRef = useRef();
  const [formArea] = Form.useForm();
  const [selectionType, setSelectionType] = useState(undefined);
  const [polygon, setPolygon] = useState({});
  const [postalCodes, setPostalCodes] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  //const initialFeaturesMemoized = useMemo(() => polygonsToGeoJSON(area), [area]);
  const initialFeaturesMemoized = useMemo(() => {
    if (area) return polygonsToGeoJSON(area);
    if (polygon && Object.keys(polygon).length > 0) return kmlPolygonsToFeatureCollection(polygon);
    return { type: 'FeatureCollection', features: [] };
  }, [area, polygon]);

  useEffect(() => {
    if (area) {
      formArea.setFieldsValue({
        name: area.name,
        carrierId: area.carrierId,
      });
      if (area.type === 'AREA') {
        setSelectionType('AREA');
        area?.area?.coordinates.forEach((coordinates, index) => {
          setPolygon((prevPolygon) => ({
            ...prevPolygon,
            [index]: coordinates,
          }));
        });
      } else if (area.type === 'POSTAL_CODES') {
        setSelectionType('POSTAL_CODES');
        setPostalCodes(area.postalCodes);
        setSelectedCountry(area.country);
      }
    } else {
      setSelectionType('AREA');
      formArea.resetFields();
    }
  }, [formArea, isModalOpen, area]);

  useEffect(() => {
    if (mapRef.current) {
      setMapBounds();
    }
  }, [polygon, mapRef.current]);

  const [viewState, setViewState] = useState({
    longitude: 5.2913,
    latitude: 52.1326,
    zoom: 6,
  });

  const onFinishForm = (values) => {
    let params = {};
    if (selectionType === 'AREA') {
      params = {
        name: values.name,
        area: polygon,
        type: 'AREA',
      };
    } else if (selectionType === 'POSTAL_CODES') {
      params = {
        name: values.name,
        postalCodes: postalCodes,
        type: 'POSTAL_CODES',
        country: selectedCountry,
      };
    }
    // If an area exists, add the id so that it will be updated instead of creating new one
    if (area) {
      params.id = area.id;
    }
    params.carrierId = values.carrierId;
    setPolygon(undefined);
    setPostalCodes([]);
    setSelectionType(undefined);
    setSelectedCountry(undefined);
    formArea.resetFields();

    handleOk && handleOk(params);
  };

  const onCancel = () => {
    setPolygon(undefined);
    setPostalCodes([]);
    setSelectionType(undefined);
    setSelectedCountry(undefined);
    formArea.resetFields();
    handleCancel && handleCancel();
  };

  const onOk = () => {
    formArea.submit();
  };

  const setMapBounds = () => {
    let bounds;

    // Check if there's valid polygon data
    if (polygon && Object.keys(polygon).length > 0) {
      let minLat = Infinity,
        maxLat = -Infinity,
        minLng = Infinity,
        maxLng = -Infinity;

      Object.values(polygon).forEach((p) => {
        p.forEach((polygon) => {
          polygon.forEach(([lng, lat]) => {
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          });
        });
      });

      // Create bounds from polygon data if valid
      bounds = new mapboxgl.LngLatBounds(new mapboxgl.LngLat(minLng, minLat), new mapboxgl.LngLat(maxLng, maxLat));
    } else {
      // Use default bounds if no valid polygon data
      bounds = new mapboxgl.LngLatBounds(
        new mapboxgl.LngLat(3.31497114423, 50.803721015),
        new mapboxgl.LngLat(7.09205325687, 53.5104033474),
      );
    }

    // Set map to bounds
    mapRef.current?.fitBounds(bounds, { padding: 60 });
  };

  const onUpdate = useCallback((e) => {
    setPolygon((currPolygon) => {
      const newPolygon = { ...currPolygon };
      for (const f of e.features) {
        if (f.properties && f.properties.id != null) {
          newPolygon[f.properties.id] = f.geometry.coordinates;
        } else {
          newPolygon[f.id] = f.geometry.coordinates;
        }
      }
      return newPolygon;
    });
  }, []);

  const onDelete = useCallback((e) => {
    setPolygon((currPolygon) => {
      const newPolygon = { ...currPolygon };
      for (const f of e.features) {
        if (f.properties && f.properties.id != null) {
          delete newPolygon[f.properties.id];
        } else {
          delete newPolygon[f.id];
        }
      }
      return newPolygon;
    });
  }, []);

  const handleAreaTypeChange = (value) => {
    setSelectionType(value);
  };

  const handleKMLUpload = async (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const parser = new DOMParser();
      const kml = parser.parseFromString(e.target.result, 'text/xml');
      const geoJSON = toGeoJSON.kml(kml);

      const newPolygon = {};
      geoJSON.features.forEach((feature, index) => {
        if (feature.geometry.type === 'Polygon') {
          newPolygon[index] = feature.geometry.coordinates;
        }
      });

      setPolygon(newPolygon);
    };
    reader.readAsText(file);

    // Prevent default Ant Design upload behavior
    return false;
  };

  return (
    <div className="DeliveryAreaForm">
      <Row gutter={16}>
        <Col span={6}>
          <Form
            form={formArea}
            scrollToFirstError
            requiredMark={false}
            className="FormContent"
            layout="vertical"
            onFinish={onFinishForm}
            initialValues={null}
          >
            <Row gutter={[16, 16]}>
              {isNewArea && (
                <Col span={24}>
                  <Form.Item label="How do you want to define the area?" name="areaType">
                    <Radio.Group
                      defaultValue="AREA"
                      buttonStyle="solid"
                      size="large"
                      style={{ width: '100%' }}
                      onChange={(e) => handleAreaTypeChange(e.target.value)}
                    >
                      <Radio.Button value="AREA" style={{ width: '50.00%', textAlign: 'center' }}>
                        Draw Area
                      </Radio.Button>
                      <Radio.Button value="POSTAL_CODES" style={{ width: '50.00%', textAlign: 'center' }}>
                        Postal Codes
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              {selectionType === 'AREA' && (
                <Col span={24}>
                  <Form.Item label="Upload KML File">
                    <Upload accept=".kml" beforeUpload={handleKMLUpload} showUploadList={false}>
                      <Button icon={<InboxOutlined />}>Upload KML</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              )}

              <Col span={24}>
                <Form.Item label="Delivery Area Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                  <Input size="large" placeholder="Delivery Area Name" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Carrier"
                  name="carrierId"
                  rules={[{ required: false }]}
                  //initialValue={area?.carrierId}
                >
                  <Select size="large" placeholder="Select a carrier">
                    {carriers.map((carrier) => (
                      <Select.Option key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col span={18}>
          {selectionType === 'AREA' && (
            <ReactMapGL
              ref={mapRef}
              {...viewState}
              width="100%"
              height="100%"
              mapStyle="mapbox://styles/maurice-sparqle/cll2ad40r00e801pd7cvn31eo"
              mapboxAccessToken={process.env.REACT_APP_MAP_BOX_KEY}
              onMove={(evt) => setViewState(evt.viewState)}
              onLoad={setMapBounds}
            >
              <DrawControl
                position="top-left"
                initialFeatures={initialFeaturesMemoized}
                displayControlsDefault={false}
                controls={{
                  polygon: true,
                  trash: true,
                }}
                defaultMode="draw_polygon"
                onCreate={onUpdate}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            </ReactMapGL>
          )}
          {selectionType === 'POSTAL_CODES' && (
            <PostalCodeInput
              className="PostalCodeInput"
              postalCodes={postalCodes}
              setPostalCodes={setPostalCodes}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
            />
          )}
        </Col>
      </Row>
      <div className="FormFooter">
        <Button className="ButtonCancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="CreateButton ButtonPrimary" type="primary" onClick={onOk}>
          {area ? 'Edit' : 'Create'}
          <ReactSVG className="icon" src="/icons/done.svg" />
        </Button>
      </div>
    </div>
  );
};

export default DeliveryAreaForm;
